import DiscountsTabInTxn from 'components/Profile/components/DiscountsTabInTxn';
import {
  ADD_TRANSACTION_RECEIVER_GET_NAME,
  ADD_TRANSACTION_YOU_SEND_NAME,
  DESKTOP_MIN_WIDTH,
  TABLET_MAX_WIDTH,
} from 'constants';
import Cookies from 'helpers/cookies';
import { MAKE_PAYMENT_KEY } from 'hooks/form-add-transaction/useAddTransaction';
import useFxAddTransaction from 'hooks/fx/useFxAddTransaction';
import useFxFormAddTransactionStore from 'hooks/fx/useFxFormAddTransactionStore';
import useLang from 'hooks/useLang';
import useQuery from 'hooks/useQuery';
import useStore from 'hooks/useStore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Navbar from 'react-bootstrap/Navbar';
import Offcanvas from 'react-bootstrap/Offcanvas';
import styled from 'styled-components';
import DiscountApplied from '../DiscountApplied';
import DiscountAvailableSection from '../DiscountAvailableSection';
import DiscountLoading from '../DiscountLoading';
import { getDiscountDrawerDisplayState } from './displayState';
import { useMinVisibleFlag } from '../../hooks/useMinVisibleFlag';
import { paragraph700Mixin } from 'styles';
import usePage from 'hooks/usePage';

const DiscountsDrawer = ({
  onApplyError,
  isFx = false,
  isEmptyVisible,
  isDisabled,
  isDiscountResultReady,
  handleApplyDiscount,
  failedDiscountRecovery,
  activeListFetchFailure,
  onDrawerOpen,
}) => {
  const { t } = useLang();
  const { state } = useStore();
  let { addTransactionInformation } = state;

  const { isAddTransactionPage } = usePage();

  const { addTransactionInformation: fxAddTransactionInformation } =
    useFxFormAddTransactionStore();

  addTransactionInformation = isFx
    ? fxAddTransactionInformation
    : addTransactionInformation;

  const { paymentMethod: paymentMethodStore, remittance } =
    addTransactionInformation || {};
  const sendAmount = remittance?.[ADD_TRANSACTION_YOU_SEND_NAME];
  const receiverAmount = remittance?.[ADD_TRANSACTION_RECEIVER_GET_NAME];

  const { isMakePayment: isFxMakePayment } = useFxAddTransaction();

  const query = useQuery();
  const refNumber = query.get('refNumber');
  const isMakePayment = isFx
    ? isFxMakePayment
    : Cookies.get(MAKE_PAYMENT_KEY) || refNumber;

  const {
    discountsAvailable: discountsAvailableStore,
    discountsInactive,
    discountApplied,
  } = paymentMethodStore || {};
  const displayState = useMemo(
    () =>
      getDiscountDrawerDisplayState({
        discountsAvailable: discountsAvailableStore,
        discountsInactive,
        discountApplied,
        isDisabled,
        isEmptyVisible,
        isMakePayment,
        sendAmount,
        receiverAmount,
        failedDiscountRecovery,
        activeListFetchFailure,
        isDiscountResultReady,
      }),
    [
      discountsAvailableStore,
      discountsInactive,
      discountApplied,
      isDisabled,
      isEmptyVisible,
      isMakePayment,
      sendAmount,
      receiverAmount,
      failedDiscountRecovery,
      activeListFetchFailure,
      isDiscountResultReady,
    ],
  );
  const {
    discountsAvailable,
    discountsInactive: displayDiscountsInactive,
    isAvailableSectionVisible,
    isAppliedSectionVisible,
    isDrawerHidden,
    isApplyingDiscount,
    availableSectionAvailability,
    availableSectionFailure,
  } = displayState;

  // The loading placeholder (the #DFE0FF4D background) is driven by the actual
  // validation/apply request, not the earlier pending window, so it does not
  // flash on the immediate first landing before a request has fired. Held for a
  // minimum duration so a quick request still surfaces the loading state, and
  // aligned with DiscountLoading (same signal + hold) so they do not desync.
  const isLoadingSectionActive = useMinVisibleFlag(isApplyingDiscount);

  const [isShowNavbar, setShowNavbar] = useState(false);

  const handleOnToggleNavbar = useCallback(
    (e) => {
      if (isAppliedSectionVisible) {
        if (e && !['svg', 'path'].includes(e.target?.tagName)) return;
      }

      // Only on the open transition (false->true): soft-dismiss the discount
      // eligibility toast, matching the amount-change dismiss in Remittance.
      // Not fired on close. isShowNavbar is a dep so this reads the current
      // value at click time rather than a stale closure.
      if (!isShowNavbar) {
        onDrawerOpen?.();
      }

      setShowNavbar((currentIsShowNavbar) => !currentIsShowNavbar);
    },
    [isAppliedSectionVisible, isShowNavbar, onDrawerOpen],
  );

  const handleAvailableSectionClick = useCallback(
    (event) => {
      event?.stopPropagation?.();
      handleOnToggleNavbar();
    },
    [handleOnToggleNavbar],
  );

  useEffect(() => {
    if (discountApplied) {
      setShowNavbar(false);
    }
  }, [discountApplied]);

  // Close the drawer when a manual apply hits a transport/system failure. The
  // system-error modal itself is surfaced by checkDiscountAppliedValid; this
  // only ensures the drawer does not stay open behind it. Preserve the
  // undefined case so DiscountTicket's handleApplyDiscountDefault fallback still
  // applies when no handler was provided.
  const handleApplyDiscountWithSystemClose = useMemo(
    () =>
      handleApplyDiscount
        ? (payload) =>
            handleApplyDiscount({
              ...payload,
              onSystemError: () => setShowNavbar(false),
            })
        : undefined,
    [handleApplyDiscount],
  );

  const navbarToggle = useMemo(() => {
    if (isAvailableSectionVisible) {
      return (
        <DiscountAvailableSection
          onClick={handleAvailableSectionClick}
          availability={availableSectionAvailability}
          failure={availableSectionFailure}
        />
      );
    }

    if (isAppliedSectionVisible) {
      return (
        <DiscountApplied
          isFx={isFx}
          isMakePayment={isMakePayment}
          onToggleNavbar={handleOnToggleNavbar}
          onDismissEligibilityToast={onDrawerOpen}
        />
      );
    }

    return <></>;
  }, [displayState, handleOnToggleNavbar, isFx, isMakePayment, onDrawerOpen]);

  // While a validation/apply request is in flight, swap the live toggle for a
  // stable "Offer" header on the add-transaction page (nothing elsewhere); the
  // loading placeholder background + DiscountLoading carry the loading visual.
  const navbarToggleContent = !isLoadingSectionActive ? (
    navbarToggle
  ) : isAddTransactionPage ? (
    <OfferLabel>{t('label_offer')}</OfferLabel>
  ) : null;

  return (
    <>
      {/* Mounted loading component: displays only once a discount
          validation/apply request is actually in flight (isApplyingDiscount),
          not during the earlier pending window (which is covered by the
          #DFE0FF4D background). DiscountLoading applies its own minimum-visible
          hold so a fast request stays perceptible. */}
      <DiscountLoading isOpen={isApplyingDiscount} />
      <style>
        {`
          .discount-drawer-offcanvas {
            width: 100%!important;
            height: 100%;
            border: none!important;
          }
          .discount-drawer-offcanvas-backdrop {
            opacity: 0.7!important;
          }
          @media screen and (max-width: ${TABLET_MAX_WIDTH}px) {
            .discount-drawer-offcanvas {
              height: calc(100% - 70px);
              margin-top: 70px;
            }
          }
          @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
            .discount-drawer-offcanvas {
              width: 375px!important;
            }
          }
        `}
      </style>
      <DiscountsDrawerStyled
        data-testid="discounts-drawer"
        data-loading-section={String(isLoadingSectionActive)}
        $isLoadingSectionVisible={isLoadingSectionActive}
        style={{
          // Hide on the immediate first landing: show the drawer only once the
          // validation request is actually in flight (loading placeholder) or
          // the discount result has settled. This keeps the section and its
          // background-color state from appearing instantly on step-2 entry.
          display:
            (isDrawerHidden ||
              (!isLoadingSectionActive && !isDiscountResultReady)) &&
            'none',
        }}
      >
        <NavbarStyled
          expand={false}
          expanded={isShowNavbar}
          onToggle={handleOnToggleNavbar}
        >
          <NavbarToggle aria-controls="offcanvasNavbar-expand-false">
            {navbarToggleContent}
          </NavbarToggle>
          <NavbarOffcanvas
            id="offcanvasNavbar-expand-false"
            aria-labelledby="offcanvasNavbarLabel-expand-false"
            placement="end"
            className="discount-drawer-offcanvas"
            backdropClassName="discount-drawer-offcanvas-backdrop"
          >
            <OffcanvasHeader closeButton>
              <OffcanvasTitle id="offcanvasNavbarLabel-expand-false">
                {t('label_offers')}
              </OffcanvasTitle>
            </OffcanvasHeader>
            <OffcanvasBody>
              <ContentBody>
                <DiscountsTabInTxn
                  itemsActive={discountsAvailable}
                  itemsInactive={displayDiscountsInactive}
                  onApplyError={onApplyError}
                  handleApplyDiscount={handleApplyDiscountWithSystemClose}
                />
              </ContentBody>
            </OffcanvasBody>
          </NavbarOffcanvas>
        </NavbarStyled>
      </DiscountsDrawerStyled>
    </>
  );
};

const DiscountsDrawerStyled = styled.div`
  ${(props) =>
    props.$isLoadingSectionVisible &&
    `
      min-height: 44px;
      border-radius: 12px;
      background: #DFE0FF4D;
  `};
`;

const NavbarStyled = styled(Navbar)`
  display: initial;
  padding: 0;
`;
const NavbarToggle = styled(Navbar.Toggle)`
  padding: 0;
  border: none;
  width: 100%;

  &:focus {
    border: none;
    box-shadow: none;
  }

  .navbar-toggler-icon {
    display: none;
  }
`;

const NavbarOffcanvas = styled(Navbar.Offcanvas)``;
const OffcanvasHeader = styled(Offcanvas.Header)`
  height: 70px;
  padding: 16px 24px;
  border-bottom: 1px solid #eef2f5;

  & button {
    background-size: 14px;
    color: var(--ds-c-blue);
    filter: var(--ds-f-c-blue);
    opacity: 1;

    &:hover {
      color: var(--ds-c-blue-hover);
    }

    &:focus {
      outline: none !important;
      border: none !important;
      box-shadow: none !important;
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    height: 56px;
  }
`;
const OffcanvasTitle = styled(Offcanvas.Title)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  display: flex;
  align-items: center;
  color: var(--ds-c-blue);
  text-transform: uppercase;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;
const OffcanvasBody = styled(Offcanvas.Body)`
  padding: 24px;
`;

const ContentBody = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const OfferLabel = styled.div`
  ${paragraph700Mixin};
  position: absolute;
  top: -24px;
`;

export default DiscountsDrawer;
